import { useEffect, useState } from 'react';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';

export type AdminUser = {
  email: string;
  displayName: string;
  role: string;
};

type AuthState = 'checking' | 'anonymous' | 'authenticated';

async function fetchCurrentUser() {
  const response = await fetch('/api/admin/session', {
    credentials: 'same-origin'
  });

  if (!response.ok) {
    return null;
  }

  const json = (await response.json()) as { user?: AdminUser };

  return json.user ?? null;
}

export function AdminApp() {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    let isMounted = true;

    fetchCurrentUser()
      .then((currentUser) => {
        if (!isMounted) {
          return;
        }

        setUser(currentUser);
        setAuthState(currentUser ? 'authenticated' : 'anonymous');
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setUser(null);
        setAuthState('anonymous');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  async function handleLogout() {
    await fetch('/api/admin/logout', {
      method: 'POST',
      credentials: 'same-origin'
    });
    setUser(null);
    setAuthState('anonymous');
  }

  function handleAuthenticated(nextUser: AdminUser) {
    setUser(nextUser);
    setAuthState('authenticated');
  }

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <a href="/">Society of Mystics</a>
        <span>{user ? user.displayName : 'Cloudflare Pages + D1'}</span>
      </header>
      <section className="admin-hero">
        <p className="eyebrow">Production Management</p>
        <h1>Society of Mystics Admin</h1>
        <p>
          A lean management surface for content, downloadable resources, and contact submissions.
        </p>
      </section>
      {authState === 'checking' ? <section className="admin-panel login-panel">Checking session...</section> : null}
      {authState === 'anonymous' ? <LoginPage onAuthenticated={handleAuthenticated} /> : null}
      {authState === 'authenticated' && user ? <DashboardPage user={user} onLogout={handleLogout} /> : null}
    </main>
  );
}
